import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { HttpClient, APIResponseType } from '../types';
import { handleLogout, header } from 'utils/auth';
import { getAgentType, shouldIncludeAgentTypeInUrl } from 'app/utils/agentType';
import { ensureCsrfToken } from 'utils/csrf';

export class AxiosAdapter implements HttpClient {
  private axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  });

  // Interceptors for request and response
  constructor() {
    this.axiosInstance.interceptors.request.use(
      async config => {
        const method = (config.method || 'get').toUpperCase();
        if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
          try {
            const csrf = await ensureCsrfToken();
            if (csrf) config.headers['X-CSRF-Token'] = csrf;
          } catch (e) {
            console.warn('CSRF for request failed', e);
          }
        }
        const agentType = getAgentType();
        if (agentType && shouldIncludeAgentTypeInUrl(config.url || '')) {
          const existingParams = (config.params || {}) as Record<string, any>;
          const hasInParams =
            existingParams && Object.prototype.hasOwnProperty.call(existingParams, 'agent_type');
          const hasInUrl = !!(config.url && /[?&]agent_type=/.test(config.url));

          if (!hasInParams && !hasInUrl) {
            config.params = { ...existingParams, agent_type: agentType };
          }
        }
        const auth = header();
        if (auth) config.headers['Authorization'] = auth;
        return config;
      },
      error => {
        return Promise.reject(error);
      },
    );

    this.axiosInstance.interceptors.response.use(
      (response) => {
        // You can modify the response here
        return response;
      },
      (error) => {
        if (error?.response?.status === 401) {
          handleLogout();
        }
        return this.handleError(error);
      }
    );
  }

  // Method to handle errors
  private handleError(error: any): Promise<never> {
    // Cancelled requests (axios cancel / AbortController) must bubble up
    // as-is so react-query / consumers can treat them as cancellations,
    // not as real errors that would show in the dev runtime overlay.
    if (
      axios.isCancel(error) ||
      error?.name === 'CanceledError' ||
      error?.name === 'AbortError' ||
      error?.code === 'ERR_CANCELED'
    ) {
      return Promise.reject(error);
    }

    if (error.response) {
      console.error('Response Error:', error.response.data);
      const rejected = new Error(
        error.response.data?.message || 'An error occurred',
      ) as Error & { statusCode?: number; statusText?: string };
      rejected.statusCode = error.response.status;
      rejected.statusText = error.response.statusText;
      return Promise.reject(rejected);
    } else if (error.request) {
      console.error('Request Error:', error.request);
      return Promise.reject(new Error('No response received from the server'));
    } else {
      console.error('Error:', error?.message);
      return Promise.reject(new Error(error?.message || 'An error occurred'));
    }
  }

  // Generic method to make GET requests
  async get<T>(url: string, params?: Record<string, any>): Promise<APIResponseType<T>> {
    try {
      const response: AxiosResponse<APIResponseType<T>> = await this.axiosInstance.get(url, { params });
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Generic method to make POST requests
  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponseType<T>> {
    try {
      const response: AxiosResponse<APIResponseType<T>> = await this.axiosInstance.post(url, data, config);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Generic method to make PUT requests
  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponseType<T>> {
    try {
      const response: AxiosResponse<APIResponseType<T>> = await this.axiosInstance.put(url, data, config);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Generic method to make DELETE requests
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<APIResponseType<T>> {
    try {
      const response: AxiosResponse<APIResponseType<T>> = await this.axiosInstance.delete(url, config);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }
}